import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

/* Editable assessment CONTENT (pure text only) — the 28-cell Goal×IR persona matrix
   and the 7 risk-band labels shown on the Readiness Journey results.

   IMPORTANT: this is display text ONLY. No scoring thresholds / weights live here —
   those stay hardcoded in CapitalAssessment.tsx and the backend. If the Firestore
   doc `assessmentContent/current` is missing or malformed, we fall back to these
   defaults, so behaviour is identical to before until an admin edits it. */

export type PersonaCell = [string, string]; // [name, description]
export type AssessmentContent = {
  personas: Record<string, PersonaCell[]>; // keyed R1..R7, each 4 cols in goal order G1,G2,G3,G4
  bandLabels: Record<string, string>;      // keyed R1..R7
};

// Goal column order + human labels (for the editor UI).
export const GOAL_COLUMNS = [
  { key: 'G1', label: 'Capital Readiness' },
  { key: 'G2', label: 'MVP Launch' },
  { key: 'G3', label: 'Revenue Growth' },
  { key: 'G4', label: 'Governance & Structure' },
];
export const R_CODES = ['R1', 'R2', 'R3', 'R4', 'R5', 'R6', 'R7'];

export const DEFAULT_BAND_LABELS: Record<string, string> = {
  R1: 'Critical Risk', R2: 'Very High Risk', R3: 'High Risk', R4: 'Medium Risk',
  R5: 'Semi-Ready', R6: 'Investor-Ready', R7: 'Exceptional',
};

export const DEFAULT_PERSONA: Record<string, PersonaCell[]> = {
  R1: [['Foundation Dreamer', "You're laying the earliest bricks of your investor story."], ['Prototype Dreamer', "You're shaping your first vision into reality."], ['Growth Dreamer', "You're imagining your first customers."], ['Structure Dreamer', "You're starting to sketch the rules of your game."]],
  R2: [['Capital Explorer', 'Learning the ropes of fundraising and value creation.'], ['MVP Explorer', 'Testing bold ideas through experiments.'], ['Market Explorer', 'Searching for first traction opportunities.'], ['Governance Explorer', 'Curious about setting up the right foundation.']],
  R3: [['Capital Builder', 'Beginning to assemble your deck and valuation story.'], ['MVP Builder', 'Turning sketches into prototypes.'], ['Market Builder', 'Piecing together funnels and early sales.'], ['Governance Builder', 'First steps in structuring equity and roles.']],
  R4: [['Capital Shaper', "You've got a draft story; it's time to sharpen it."], ['MVP Shaper', 'Your product is forming; refinement is next.'], ['Growth Shaper', 'Customers are coming in; now focus on retention.'], ['Governance Shaper', 'Early frameworks exist; make them stronger.']],
  R5: [['Capital Challenger', 'Your materials are strong, just need polishing.'], ['MVP Challenger', 'Your build is near-ready to impress users.'], ['Growth Challenger', 'Revenue playbook in progress, scaling soon.'], ['Governance Challenger', 'Governance is working, needs fine-tuning.']],
  R6: [['Capital Trailblazer', "You're compelling to investors; next is momentum."], ['MVP Trailblazer', "You've got a validated product, ready to scale."], ['Growth Trailblazer', 'Revenue traction is solid; expansion awaits.'], ['Governance Trailblazer', 'Systems are credible; primed for scale.']],
  R7: [['Capital Visionary', 'An inspiring investment story; fundable and future-ready.'], ['MVP Visionary', 'A standout product with wow-factor.'], ['Growth Visionary', 'A growth engine admired by peers and investors.'], ['Governance Visionary', 'A model company with exemplary structure.']],
};

export const DEFAULT_ASSESSMENT_CONTENT: AssessmentContent = {
  personas: DEFAULT_PERSONA,
  bandLabels: DEFAULT_BAND_LABELS,
};

// Merge a loaded persona map over the defaults, validating each R row is 4 valid cells.
export function mergePersonas(p?: Record<string, any>): Record<string, PersonaCell[]> {
  if (!p || typeof p !== 'object') return DEFAULT_PERSONA;
  const out: Record<string, PersonaCell[]> = {};
  for (const r of R_CODES) {
    const rows = p[r];
    out[r] = Array.isArray(rows) && rows.length === 4 && rows.every((x: any) => Array.isArray(x) && x.length >= 2)
      ? rows.map((x: any) => [String(x[0] ?? ''), String(x[1] ?? '')] as PersonaCell)
      : DEFAULT_PERSONA[r];
  }
  return out;
}

export function useAssessmentContent(): AssessmentContent {
  const [c, setC] = useState<AssessmentContent>(DEFAULT_ASSESSMENT_CONTENT);
  useEffect(() => {
    const ref = doc(db, 'assessmentContent', 'current');
    return onSnapshot(ref, snap => {
      if (!snap.exists()) return; // no doc → keep defaults
      const d = snap.data() as Partial<AssessmentContent>;
      setC({
        personas: mergePersonas(d.personas),
        bandLabels: { ...DEFAULT_BAND_LABELS, ...(d.bandLabels && typeof d.bandLabels === 'object' ? d.bandLabels : {}) },
      });
    }, () => { /* permission/transient errors → keep defaults */ });
  }, []);
  return c;
}
